import type { BusinessApiClient } from '@infra/api/BusinessApiClient';
import {
  parseBootstrapResponse,
  parsePullResponse,
  parsePushResponse,
  parseStatusResponse,
  SyncProtocolError,
  type SyncPushRequest,
} from './SyncProtocol';
import { SyncRequestError, type SyncApiPort } from './SyncPorts';

const STATUS_TIMEOUT_MS = 5_000;
const BULK_TIMEOUT_MS = 30_000;
const ADMIN_TIMEOUT_MS = 12_000;

function parsed<T>(read: () => T): T {
  try {
    return read();
  } catch (err) {
    if (err instanceof SyncProtocolError) throw new SyncRequestError(err.message, 'protocol');
    throw err;
  }
}

/** Accès HTTP réel au serveur de synchronisation (`/v1/sync/*` de la business-api). */
export function createBusinessApiSyncPort(client: BusinessApiClient): SyncApiPort {
  return {
    status: async (opts) => {
      const raw = await client.syncRequest('GET', `/v1/sync/status${opts?.includeCounts ? '?include=counts' : ''}`, {
        timeoutMs: STATUS_TIMEOUT_MS,
      });
      return parsed(() => parseStatusResponse(raw));
    },
    push: async (request: SyncPushRequest) => {
      const raw = await client.syncRequest('POST', '/v1/sync/push', { body: request, timeoutMs: BULK_TIMEOUT_MS });
      return parsed(() => parsePushResponse(raw));
    },
    pull: async ({ cursor, limit }) => {
      const query = new URLSearchParams();
      if (cursor) query.set('cursor', cursor);
      if (limit) query.set('limit', String(limit));
      const qs = query.toString();
      const raw = await client.syncRequest('GET', `/v1/sync/pull${qs ? `?${qs}` : ''}`, { timeoutMs: BULK_TIMEOUT_MS });
      return parsed(() => parsePullResponse(raw));
    },
    bootstrap: async ({ resource, after, limit }) => {
      const query = new URLSearchParams({ resource });
      if (after) query.set('after', after);
      if (limit) query.set('limit', String(limit));
      const raw = await client.syncRequest('GET', `/v1/sync/bootstrap?${query.toString()}`, { timeoutMs: BULK_TIMEOUT_MS });
      return parsed(() => parseBootstrapResponse(raw));
    },
    enable: async (consentVersion) => {
      await client.syncRequest('POST', '/v1/sync/enable', {
        body: { consent_version: consentVersion },
        timeoutMs: ADMIN_TIMEOUT_MS,
      });
    },
    disable: async () => {
      await client.syncRequest('POST', '/v1/sync/disable', { body: {}, timeoutMs: ADMIN_TIMEOUT_MS });
    },
    refreshSession: () => client.refreshSessionForSync(),
  };
}
